import {AnalogCompositionType, DocumentForm, DocumentState, EmpowermentDto, SettlementType} from '|api/commons';
import {DocumentAllowableObjectClass} from '|api/document';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DestroyRef} from '@angular/core';
import {getCustomFieldsFormArray, SharedBusinessValidators} from '|shared';
import {IczFormControl, IczFormGroup, IczValidators, TextLength} from '@icz/angular-form-elements';

/**
 * Do not add default disabled state of fields in contructor of IczFormControl. Add it in method setFileFormDisabledState
 * because disabled state of form can change when document state changes and document is reloaded.
 */
export function createDocumentDetailFormGroup(): IczFormGroup {
  return new IczFormGroup({
    assignRefNr: new IczFormControl<Nullable<string>>(),
    mainPaperComponentSheetCount: new IczFormControl<Nullable<number>>(0, []),
    paperComponentVolumeEnclosureCount: new IczFormControl<Nullable<number>>(0, []),
    paperComponentSheetEnclosureCount: new IczFormControl<Nullable<number>>(0, []),
    paperComponentSheetCount: new IczFormControl<Nullable<number>>(0, []),
    analogCompositionType: new IczFormControl<Nullable<AnalogCompositionType>>(AnalogCompositionType.SHEETS, []),
    analogComponentCount: new IczFormControl<Nullable<number>>(0, []),
    analogComponentWithDigitalContentCount: new IczFormControl<Nullable<number>>(0, []),
    digitalComponentCount: new IczFormControl<Nullable<number>>(0, []),
    nonPaperComponentCount: new IczFormControl<Nullable<number>>(0, []),
    consignmentDigitalComponentsCount: new IczFormControl<Nullable<number>>(0, []),
    nonPaperComponentTypeAndCount: new IczFormControl<Nullable<number>>(0, []),
    componentPhysicalLocation: new IczFormControl<Nullable<string>>(),
    description: new IczFormControl<Nullable<string>>(),
    documentForm: new IczFormControl<Nullable<DocumentForm>>(null, [IczValidators.required()]),
    documentState: new IczFormControl<Nullable<DocumentState>>(null),
    documentTypeId: new IczFormControl<Nullable<number>>(null),
    executionDate: new IczFormControl<Nullable<string>>(),
    fileId: new IczFormControl<Nullable<number>>(),
    id: new IczFormControl<Nullable<number>>(),
    keywordIds: new IczFormControl<Nullable<number[]>>(null),
    holderFunctionalPositionIds: new IczFormControl<Nullable<number>>(null),
    label: new IczFormControl<Nullable<string>>(),
    note: new IczFormControl<Nullable<string>>(),
    relatedEvidence: new IczFormControl<Nullable<string>>(),
    objectClass: new IczFormControl<Nullable<DocumentAllowableObjectClass>>(),
    ownerFunctionalPositionId: new IczFormControl<Nullable<number>>(null),
    packageId: new IczFormControl<Nullable<number>>(),
    pageCount: new IczFormControl<Nullable<number>>(),
    refNumber: new IczFormControl<Nullable<string>>(null),
    resolutionDate: new IczFormControl<Nullable<string>>(),
    securityCategoryId: new IczFormControl<Nullable<number>>(null),
    senderRefNr: new IczFormControl<Nullable<string>>(),
    sourceUid: new IczFormControl<Nullable<string>>(),
    subject: new IczFormControl<Nullable<string>>('', [IczValidators.required(), IczValidators.maxLength(100)], undefined, TextLength.UNLIMITED),
    entityClassId: new IczFormControl<Nullable<number>>(null),
    uid: new IczFormGroup({
      uid: new IczFormControl<Nullable<string>>(null),
    }),
    auditInfo: new IczFormGroup({
      createdAt: new IczFormControl<Nullable<string>>(null),
      createdBy: new IczFormControl<Nullable<string>>(),
      modifiedAt: new IczFormControl<Nullable<string>>(null),
    }),
    deactivateAuditInfo: new IczFormGroup({
      deactivationDate: new IczFormControl<Nullable<string>>(null),
      deactivationReason: new IczFormControl<Nullable<string>>(),
    }),
    filingRegisterId: new IczFormControl<Nullable<number>>(null),
    filingRegisterRegDate: new IczFormControl<Nullable<string>>(null),
    empowerment: new IczFormControl<Nullable<EmpowermentDto>>(null),
    settlementFunctionalPositionId: new IczFormControl<Nullable<number>>(null),
    settlementDate: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    settlementContent: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    settlementReason: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    settlementType: new IczFormControl<Nullable<SettlementType>>(null),
    issdId: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    yearOfRetentionPeriodStart: new IczFormControl<Nullable<number>>(null),
    triggerEventCheckYear: new IczFormControl<Nullable<number>>({value: null, disabled: true}),
    disposalYear: new IczFormControl<Nullable<number>>({value: null, disabled: true}),
    customFieldValues: getCustomFieldsFormArray(),
    externalRegisterMoveDate: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    externalRegisterMoveReason: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    lossDate: new IczFormControl<Nullable<string>>(null),
    lossReason: new IczFormControl<Nullable<string>>(),
    disposalOperationCode: new IczFormControl<Nullable<string>>({value: null, disabled: true}),

    // discovered fields
    retentionPeriod: new IczFormControl<Nullable<string>>(null),
    disposalScheduleId: new IczFormControl<Nullable<string>>(null),
    externalRetentionTriggerIds: new IczFormControl<Nullable<string>>(null),
    entityClassValidFrom: new IczFormControl<Nullable<string>>(null),
  });
}

export function setDocumentFormDisabledState(documentForm: IczFormGroup) {
  const fieldsToBeDisabled = [
    'mainPaperComponentSheetCount',
    'paperComponentVolumeEnclosureCount',
    'paperComponentSheetEnclosureCount',
    'paperComponentSheetCount',
    'analogComponentCount',
    'digitalComponentCount',
    'nonPaperComponentTypeAndCount',
    'nonPaperComponentCount',
    'analogComponentWithDigitalContentCount',
    'documentForm',
    'documentState',
    'holderFunctionalPositionIds',
    'ownerFunctionalPositionId',
    'refNumber',
    'uid.uid',
    'auditInfo.createdAt',
    'auditInfo.modifiedAt',
    'filingRegisterId',
    'filingRegisterRegDate',
    'settlementFunctionalPositionId',
    'settlementType',
    'settlementDate',
    'disposalOperationCode',
    'disposalYear',
    'retentionPeriod',
    'disposalSchedule',
    'entityClassValidFrom',
    'issdId',
    'externalRegisterMoveDate',
    'externalRegisterMoveReason',
  ];
  fieldsToBeDisabled.forEach(fieldName => {
    documentForm.get(fieldName)?.disable();
  });
}

// this is currently not used anywhere, however it SHOULD likely be used in analog create form component
export function setAnalogValidators(form: IczFormGroup, ctx: DestroyRef) {
  form.get('documentForm')!.valueChanges.pipe(
    takeUntilDestroyed(ctx),
  ).subscribe(val => {
    if (val === DocumentForm.ANALOG || val === DocumentForm.HYBRID) {
      form.get('paperComponentVolumeEnclosureCount')!.setValidators([IczValidators.required(), IczValidators.min(1)]);
      form.get('paperComponentSheetEnclosureCount')!.setValidators([IczValidators.required(), IczValidators.min(0)]);
      form.get('paperComponentSheetCount')!.setValidators([IczValidators.required(), IczValidators.min(0)]);
      form.get('analogCompositionType')!.setValidators([IczValidators.required()]);
      form.setValidators(SharedBusinessValidators.analogVolumesFormValidator);
    } else {
      form.setValidators(null);
      const fieldsToStopValidating = [
        'paperComponentVolumeEnclosureCount', 'paperComponentSheetEnclosureCount',
        'paperComponentSheetCount', 'analogCompositionType'];
      fieldsToStopValidating.forEach((field: string) => {
        form.get(field)!.setValidators(null);
        form.get(field)!.updateValueAndValidity();
      });
    }
  });
}
